import { dateFormat, numberFormat } from 'shared/util'
import { Box, Stack, Typography } from '@mui/material'

import { ValueSpan } from 'components/controls/valueSpan'
import { KojinAvatar } from 'features/kojin/components/avatar'
import { InterestRadar } from 'features/kojin/components/interestRadar'

import { getWebLogTags } from 'interface/kojin/log'
import { GetAuthor } from 'interface/author'
import { AuthorType, ListArticleByAuthor } from 'interface/article'

import { KojinPrintFormat, KojinPrintPreProcessParams, KojinPrintRecordParams } from '../types'

import {
  FirstAuthorIcon,
  LastAuthorIcon,
  SecondAuthorIcon,
  SingleAuthorIcon,
} from 'shared/icons'

const preProcess = async ({
  apiAccess,
  search,
  kojin
}: KojinPrintPreProcessParams) => {
  if (!kojin.authorId) return null
  const author = await GetAuthor(apiAccess, search.searchId, kojin.authorId)
  author.article = (await ListArticleByAuthor(apiAccess, search.searchId, kojin.authorId, { updateTime: search.updateTime })).list
  const tags = kojin.personalId ? await getWebLogTags(apiAccess, kojin.personalId) : undefined
  return {
    authorId: kojin.authorId,
    author,
    tags
  }
}

const record = ({
  search,
  kojin,
  counter,
  preProcessData,
  count
}: KojinPrintRecordParams) => {
  const reportData = !kojin.authorId ? undefined : (
    preProcessData.find(author => author.authorId === kojin.authorId)
  )
  const actorTexts: string[] = []
  if (kojin.actor?.isComActor) actorTexts.push('CareNet.com出演')
  if (kojin.actor?.isDvdActor) actorTexts.push('DVD出演')
  if (kojin.actor?.isTvActor) actorTexts.push('CareNeTV出演')
  if (kojin.picksExpert) actorTexts.push("Doctors'Picks Expert Picker")

  const drugTags = reportData?.tags?.drug ?? []
  const author = reportData?.author

  let maxDiseaseScore = 0
  let searchsDiseaseInterests: {
    icd10code: string
    diseaseName: string
    score: number
  }[] | undefined = undefined
  if (
    search.disease &&
    search.disease.length > 0 &&
    kojin.diseases &&
    kojin.diseases.length > 0
  ) {
    maxDiseaseScore = kojin.diseases.reduce((max, { score }) => Math.max(max, score), 0)
    searchsDiseaseInterests = search.disease.flatMap((disease) => {
      if (!disease.icd10code) return []
      const icd10code = disease.icd10code
      let score = 0
      kojin.diseases?.filter((d) => disease.icd10code?.startsWith(d.icd10code)).forEach((d) => {
        score += d.score
      })
      return {
        icd10code: icd10code,
        diseaseName: disease.diseaseName ?? '??',
        score
      }
    })
  }
  return (
    <Stack
      direction="column"
      component="div"
      style={{
        pageBreakAfter: counter < count - 1 ? 'always' : 'auto',
        minHeight: '180mm',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        padding={1}
        borderBottom="3px solid #ad0026"
        alignItems="flex-start"
      >
        <KojinAvatar kojin={kojin} size="medium" />

        <Stack direction="column">
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
          >
            {kojin.kojinCode ? (
              <Typography
                variant="h4"
                color="text.primary"
                component="div"
                whiteSpace="nowrap"
              >
                {kojin.isiName}&emsp;先生
              </Typography>
            ) : (
              <Typography
                variant="h4"
                color="text.secondary"
                component="div"
                whiteSpace="nowrap"
                marginLeft={1}
                fontStyle="italic"
              >
                {kojin.isiName}&emsp;先生
              </Typography>
            )}
            <Box flexGrow={1} />

            {actorTexts.length > 0 && (
              <Typography variant="h6" color="#ad0026" component="div" flexGrow={1}>
                {actorTexts.join('／')}
              </Typography>
            )}
            {kojin.nenrei !== null && kojin.nenrei !== undefined && (
              <Typography
                variant="h6"
                color="text.primary"
                component="div"
                whiteSpace="nowrap"
              >
                {kojin.nenrei}歳
              </Typography>
            )}
            <Typography
              variant="h6"
              color="text.primary"
              component="div"
              whiteSpace="nowrap"
            >
              {kojin.seibetuCode === '1' ? '男性' :
              kojin.seibetuCode === '2' ? '女性' : 'その他／不明'}
            </Typography>
          </Stack>

          {kojin.organ?.map(organ => (
            <Stack key={`organ-${organ.sisetuCode}`} direction="row" flexWrap="wrap">
              <Typography variant="body1" color="text.secondary" component="div">
                {organ.sisetuName}
              </Typography>
              {organ.bukaName && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  component="div"
                >
                  {organ.bukaName}
                </Typography>
              )}
              {organ.yakusyokuName && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  component="div"
                >
                  {organ.yakusyokuName}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} padding={2} flexGrow={1}>
        {searchsDiseaseInterests && (searchsDiseaseInterests?.length ?? 0) > 0 && (
          <Box width={400}>
            <InterestRadar
              list={searchsDiseaseInterests.map((disease) => ({
                label: disease.diseaseName,
                min: 0,
                max: maxDiseaseScore,
                value: disease.score,
              }))}
              animation={false}
              width={400}
              height={200}
              nameGap={10}
              radius={70}
            />
          </Box>
        )}
        <Box flexGrow={1} />

        {search.drug ? (
          <Stack direction="column" spacing={1}>
            {search.drug.map((drug, i) => {
              const drugNames = drug.drugName.split('/')
              const score = drugNames.reduce(
                (m, drugName) => drugTags.reduce(
                  (n, tag) => {
                    if (tag.tag === drugName) return n + tag.count
                    return n
                  }, m
                ),
                0
              )
              return (
                <Stack key={`tag-drug-${i}`} direction="row" spacing={1}>
                  <Typography
                    variant="body1"
                    component="div"
                    whiteSpace="nowrap"
                    color={score > 0 ? 'text.primary' : 'text.secondary'}
                    flexGrow={1}
                  >
                    {drug.drugName}
                  </Typography>
                  <Typography
                    variant="body1"
                    component="div"
                    whiteSpace="nowrap"
                    color={score > 0 ? 'text.primary' : 'text.secondary'}
                    textAlign="right"
                    minWidth="6em"
                    paddingX={2}
                  >
                    {score}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        ) : null}
      </Stack>

      {(author && (author.counts.total > 0)) ? (
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <ValueSpan
              text={numberFormat(author.counts.total)}
              prefix="全"
              suffix="件"
            />
            {author.counts.single > 0 && (
              <ValueSpan
                text={numberFormat(author.counts.single)}
                prefix="単独著者"
                suffix="件"
              />
            )}
            {author.counts.first > 0 && (
              <ValueSpan
                text={numberFormat(author.counts.first)}
                prefix="先頭著者"
                suffix="件"
              />
            )}
            {author.counts.second > 0 && (
              <ValueSpan
                text={numberFormat(author.counts.second)}
                prefix="第二著者"
                suffix="件"
              />
            )}
            {author.counts.last > 0 && (
              <ValueSpan
                text={numberFormat(author.counts.last)}
                prefix="責任著者"
                suffix="件"
              />
            )}
            {/*<ValueSpan text={util.numberFormat(author.score)} suffix='pt'/>
              <ValueSpan text={util.numberFormat(author.rank)} prefix='全国' suffix='位'/>*/}
          </Stack>

          {author.article?.map((article, index) => (
            <Stack
              key={`article-${index}-${article.articleId}`}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              flexGrow={1}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                whiteSpace="nowrap"
              >
                #{index + 1}
              </Typography>
              {article.authorType === AuthorType.FirstAuthor ? (
                <FirstAuthorIcon />
              ) : article.authorType === AuthorType.SecondAuthor ? (
                <SecondAuthorIcon />
              ) : article.authorType === AuthorType.LastAuthor ? (
                <LastAuthorIcon />
              ) : article.authorType === AuthorType.SingleAuthor ? (
                <SingleAuthorIcon />
              ) : null}
              <Typography
                variant="body2"
                color="text.primary"
                component="div"
                flexGrow={1}
              >
                {article.titleJpn ?? article.title ?? 'no title'}
              </Typography>
              {article.referenceCount > 0 && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="div"
                  whiteSpace="nowrap"
                >
                  {numberFormat(article.referenceCount)}件の参照
                </Typography>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                whiteSpace="nowrap"
              >
                {dateFormat(article.revicedDate, { nullText: '--' })}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  )
}

export const KojinDefaultPrintFormat: KojinPrintFormat = {
  reportId: 'default',
  reportName: 'デフォルト',
  preProcess,
  record
}
